import type { ReactNode } from 'react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { detailCta } from './detail-cta';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { CategoryPills } from '~/ui-rocco/pages/home-page/components/CategoryPills';
import type { CategoryPill } from '~/ui-rocco/pages/home-page/components/CategoryPills';

export interface EditorialHeaderProps {
    /** Parenthesised eyebrow, e.g. "(01 – Projects)". */
    eyebrow: string;
    heading: string;
    categories?: CategoryPill[];
    categoriesLabel?: string;
    description?: string;
    ctaLabel?: string;
    ctaTo?: string;
    /** Custom action(s) below the description (e.g. a button row). Takes
     *  precedence over `ctaLabel`/`ctaTo` when provided. */
    actions?: ReactNode;
    /** dark = white type on a dark section; light = ink type on white. */
    theme?: 'dark' | 'light';
}

/**
 * Shared compact editorial header for the detail pages — hairline + eyebrow, a
 * giant heading, then category pills beside a description and a CTA. The compact
 * counterpart to the homepage `DiscoverHeader`. Used dark for CTA sections (the
 * product "Tagged in Projects", the professional "Talk to us") and light for the
 * numbered section headers ("(01) Selected work", "(02) Team", …). Renders a
 * `div`, so the caller owns any section wrapper / background.
 */
export const EditorialHeader = ({
    eyebrow,
    heading,
    categories = [],
    categoriesLabel = 'Popular categories:',
    description,
    ctaLabel,
    ctaTo,
    actions,
    theme = 'dark',
}: EditorialHeaderProps) => {
    const isDark = theme === 'dark';

    return (
        <div className="mx-auto w-full max-w-[120rem] max-md:px-6 max-md:pt-10 md:px-[3rem] md:pt-[3.75rem]">
            <div
                className={cn(
                    'h-px w-full',
                    isDark ? 'bg-white/40' : 'bg-black/20',
                )}
            />
            <SectionLabel
                className={cn(
                    // Mobile snaps to the shared 14px eyebrow token and the
                    // shared section rhythm (hairline → eyebrow: 16px).
                    'mt-[1.062rem] block max-md:mt-4 max-md:text-[0.875rem] md:text-[2rem]',
                    isDark ? 'text-white' : 'text-[#181818]',
                )}
            >
                {eyebrow}
            </SectionLabel>

            <h2
                className={cn(
                    // Eyebrow → heading: 16px (a tight pair), matching DiscoverHeader.
                    'm-0 font-sans font-normal max-md:mt-4 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:mt-[5.75rem] md:text-[12.5rem] md:leading-[0.92] md:tracking-[-0.12rem]',
                    isDark ? 'text-white' : 'text-[#141414]',
                )}
            >
                {heading}
            </h2>

            {/* Heading → categories: 32px; categories → description (stacked): 24px. */}
            <div className="flex max-md:mt-8 max-md:flex-col max-md:gap-6 md:mt-[16.25rem] md:gap-[28.8rem]">
                {categories.length > 0 ? (
                    <CategoryPills
                        label={categoriesLabel}
                        items={categories}
                        className="max-md:w-full max-md:gap-3.5 md:w-[29.16rem] md:gap-[1.5625rem]"
                    />
                ) : null}
                <div className="flex min-w-0 flex-col max-md:gap-5 md:w-[52.752rem] md:gap-[2.4rem]">
                    {description ? (
                        <p
                            className={cn(
                                'm-0 font-sans leading-[1.4] font-normal max-md:text-base md:text-[2.4375rem]',
                                isDark ? 'text-white' : 'text-[#1f1f21]',
                            )}
                        >
                            {description}
                        </p>
                    ) : null}
                    {actions ? (
                        actions
                    ) : ctaLabel && ctaTo ? (
                        <RoccoLink
                            to={ctaTo}
                            className={detailCta(
                                isDark ? 'filled-dark' : 'filled-light',
                                'w-fit',
                            )}
                        >
                            {ctaLabel}
                        </RoccoLink>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
